<template>
	<div id="super-bundles-page" v-cloak>
		<div class="page-shell">
			<section class="hero-card">
				<div class="hero-copy">
					<h1 class="hero-title">Super Bundles</h1>
					<p class="hero-description">
						Get more of the ContentBASE library in one move and <span class="hero-highlight">save {{discountPctValue}}</span> across every
						super bundle. Each super bundle brings several hand-picked bundles together under one fixed price so you can unlock a fuller
						collection and keep more money in your pocket.
					</p>
				</div>
			</section>

			<section class="results-shell">
				<div class="results-panel" :class="{ 'is-loading': requestsActive }">
					<div class="results-overlay" v-show="requestsActive">
						<div class="results-overlay-backdrop"></div>
						<div class="results-overlay-content">
							<img src="/img/loaders/spinner-1s-200px-purple.svg" alt="Loading super bundles">
							<span>Loading super bundles...</span>
						</div>
					</div>

					<div class="results-top-paginator" v-show="showPaginator">
						<paginator v-model="pageOptions.pageIndex" v-bind="pageOptions" @paginate="loadSelectedPage" :disabled="requestsActive"></paginator>
					</div>

					<div class="empty-state" v-if="showEmptyState">
						<p class="empty-eyebrow">Nothing Live Yet</p>
						<h3>No super bundles are published right now</h3>
						<p>Check back soon for new curated compilations.</p>
					</div>

					<div class="super-bundle-grid" v-else>
						<article class="super-bundle-card" v-for="superBundle in superBundles" :key="superBundle.id">
							<div class="ribbon-wrap a-right" v-show="ownsEntireSuperBundle(superBundle)">
								<div class="ribbon-content"><span>Owned!</span></div>
							</div>

							<a class="card-thumb-link" :href="superBundle.url">
								<div class="card-collage">
									<div class="card-collage-cell" v-for="index in 4" :key="index">
										<img
											v-if="collageImage(superBundle, index - 1) != null"
											:src="collageImage(superBundle, index - 1).url"
											:alt="superBundle.name"
											:width="collageImage(superBundle, index - 1).width"
											:height="collageImage(superBundle, index - 1).height"
											loading="lazy"
										>
										<div v-else class="card-collage-fallback"></div>
									</div>
								</div>
							</a>

							<div class="card-body">
								<div class="card-heading-row">
									<p class="card-kicker">Super Bundle</p>
									<p class="card-bundle-count">{{superBundleInventoryText(superBundle)}}</p>
								</div>

								<h3 class="card-title">
									<a :href="superBundle.url">{{superBundle.name}}</a>
								</h3>

								<div class="price-row">
									<span class="price-current">${{formatCredits(superBundle.nrCredits)}}</span>
									<span class="price-raw-struck">${{formatCredits(superBundle.nrCreditsUndiscounted)}}</span>
								</div>

								<div class="savings-row">
									<span>You save ${{formatCredits(superBundle.savings)}}!</span>
								</div>

								<p class="owned-line" v-if="isLoggedIn">
									<template v-if="ownsEntireSuperBundle(superBundle)">
										You already own all {{superBundle.nrBundles}} included bundles.
									</template>
									<template v-else>
										You already own {{nrOwnedBundles(superBundle)}} of {{superBundle.nrBundles}} bundles.
									</template>
								</p>

								<div class="bundle-preview-list">
									<div class="bundle-preview-item" v-for="bundle in previewBundles(superBundle)" :key="bundle.id">
										<span class="bundle-preview-dot"></span>
										<span class="bundle-preview-text" :title="bundle.name">{{bundle.name}}</span>
									</div>
									<div class="bundle-preview-item bundle-preview-item-muted" v-if="remainingBundleCount(superBundle) > 0">
										<span class="bundle-preview-dot"></span>
										<span class="bundle-preview-text">+{{remainingBundleCount(superBundle)}} more bundles</span>
									</div>
								</div>

								<div class="card-actions">
									<a class="button-primary" :href="superBundle.url">View Super Bundle</a>
								</div>
							</div>
						</article>
					</div>

					<div class="results-bottom-paginator" v-show="showPaginator">
						<paginator v-model="pageOptions.pageIndex" v-bind="pageOptions" @paginate="loadSelectedPage" :disabled="requestsActive"></paginator>
					</div>
				</div>
			</section>
		</div>
	</div>
</template>




<script>
	import { mixinAxios } from '@/js/shared/VueMixins.js';
	import paginator from './common/Paginator.vue';
	import { toNrDecimals } from '@/js/shared/utils';

	const PAGE_SIZE = 12;

	export default {
		mixins: [mixinAxios],
		components: {
			paginator
		},
		data: () => ({
			overviewInfo: null,
			pageOptions: {
				options: {
					texts: {
						countMultiPage: 'Showing {from} to {to} of {count} super bundles.',
						countSinglePage: '{count} super bundles.',
						countSingleItem: '1 super bundle.'
					}
				},
				nrRecords: 0,
				pageSize: PAGE_SIZE,
				pageIndex: 0,
				pageSetSize: 10,
				disabled: false,
				hidePaginator: false
			}
		}),
		mounted() {
			this.loadSelectedPage(this.pageOptions.pageIndex);
		},
		methods: {
			loadSelectedPage(pageIndex) {
				this.pageOptions.pageIndex = pageIndex ?? 0;

				return this.mixinAxios().get('/api/guest/super-bundles/GetSuperBundlesOverviewInfo', {
					params: {
						pageIndex: this.pageOptions.pageIndex,
						pageSize: this.pageOptions.pageSize
					}
				})
					.then((result) => {
						this.overviewInfo = result.data ?? {};
						this.pageOptions.nrRecords = this.paginatedSuperBundles?.nrRecords ?? 0;
					});
			},
			formatCredits(nrCredits) {
				return toNrDecimals(nrCredits ?? 0, 2, 2);
			},
			formatDiscountPct(discountPct) {
				return toNrDecimals(discountPct ?? 0, 0, 2);
			},
			collageImage(superBundle, index) {
				return superBundle?.collageImages?.[index] ?? null;
			},
			isBundleOwned(idBundle) {
				return this.ownedBundleIDs?.[idBundle] != null;
			},
			nrOwnedBundles(superBundle) {
				return (superBundle?.includedBundleIDs ?? [])
					.filter((idBundle) => this.isBundleOwned(idBundle))
					.length;
			},
			ownsEntireSuperBundle(superBundle) {
				var nrBundles = superBundle?.nrBundles ?? 0;
				return (nrBundles > 0) && (this.nrOwnedBundles(superBundle) == nrBundles);
			},
			superBundleInventoryText(superBundle) {
				const nrBundles = superBundle?.nrBundles ?? 0;
				const nrTemplates = superBundle?.nrTemplates ?? 0;
				const bundleText = (nrBundles == 1)
					? '1 bundle'
					: `${nrBundles.toLocaleString()} bundles`;
				const templateText = (nrTemplates == 1)
					? '1 template'
					: `${nrTemplates.toLocaleString()} templates`;

				return `${bundleText} ${String.fromCharCode(8226)} ${templateText}`;
			},
			previewBundles(superBundle) {
				return (superBundle?.includedBundles ?? []).slice(0, 3);
			},
			remainingBundleCount(superBundle) {
				return Math.max(0, (superBundle?.nrBundles ?? 0) - this.previewBundles(superBundle).length);
			}
		},
		computed: {
			paginatedSuperBundles() {
				return this.overviewInfo?.paginatedSuperBundles ?? null;
			},
			superBundles() {
				return this.paginatedSuperBundles?.entities ?? [];
			},
			visitorStatus() {
				return this.overviewInfo?.visitorStatus ?? {};
			},
			ownedBundleIDs() {
				return this.overviewInfo?.ownedBundleIDs ?? {};
			},
			isLoggedIn() {
				return this.visitorStatus?.isLoggedIn ?? false;
			},
			showEmptyState() {
				return !this.requestsActive && (this.pageOptions.nrRecords == 0);
			},
			showPaginator() {
				return (this.pageOptions.nrRecords > 0);
			},
			discountPctValue() {
				return `${this.formatDiscountPct(this.overviewInfo?.superBundleDiscountPct ?? 0)}%`;
			}
		}
	};
</script>




<style scoped>
	[v-cloak] {
		display: none;
	}

	#super-bundles-page {
		padding: 24px 16px 56px 16px;
	}

	.page-shell {
		max-width: 1440px;
		margin: 0 auto;
	}

	.hero-card {
		background:
			radial-gradient(circle at top right, rgba(56, 189, 248, 0.16), transparent 35%),
			linear-gradient(135deg, rgba(224, 231, 255, 0.9), rgba(255, 255, 255, 0.98));
		border: 1px solid rgba(59, 130, 246, 0.15);
		border-radius: 32px;
		padding: 28px;
	}

	.card-kicker {
		margin: 0;
		font-size: 0.85rem;
		font-weight: 800;
		letter-spacing: 0.18em;
		text-transform: uppercase;
		color: #0f766e;
	}

	.hero-title {
		margin: 0;
		font-size: clamp(2rem, 5vw, 3.35rem);
		line-height: 1;
		color: #172554;
	}

	.hero-description {
		margin: 16px 0px 0px 0px;
		font-size: 1.08rem;
		line-height: 1.75;
		color: #334155;
		max-width: none;
	}

	.hero-highlight {
		font-weight: 800;
		color: #1d4ed8;
	}

	.results-shell {
		margin-top: 28px;
	}

	.results-panel {
		position: relative;
		border: 1px solid rgba(148, 163, 184, 0.24);
		border-radius: 30px;
		background:
			radial-gradient(circle at top right, rgba(191, 219, 254, 0.22), transparent 28%),
			#ffffff;
		padding: 30px 22px 34px 22px;
		overflow: hidden;
	}

	.results-panel.is-loading {
		min-height: 180px;
	}

	.results-overlay {
		position: absolute;
		inset: 0;
		z-index: 20;
	}

	.results-overlay-backdrop {
		position: absolute;
		inset: 0;
		background: rgba(255, 255, 255, 0.68);
		backdrop-filter: blur(2px);
	}

	.results-overlay-content {
		position: absolute;
		inset: 0;
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 10px;
		padding: 24px;
		text-align: center;
		font-weight: 700;
		color: #1e3a8a;
	}

	.results-overlay-content img {
		width: 52px;
		height: 52px;
	}

	.results-top-paginator,
	.results-bottom-paginator {
		display: flex;
		justify-content: center;
		padding: 0px 18px;
	}

	.results-top-paginator {
		margin-bottom: 26px;
	}

	.results-bottom-paginator {
		margin-top: 34px;
	}

	.empty-state {
		border: 1px dashed rgba(59, 130, 246, 0.35);
		border-radius: 24px;
		padding: 56px 20px;
		text-align: center;
		background: rgba(248, 250, 252, 0.85);
	}

	.empty-eyebrow {
		margin: 0;
		font-size: 0.82rem;
		font-weight: 800;
		letter-spacing: 0.18em;
		text-transform: uppercase;
		color: #0f766e;
	}

	.empty-state h3 {
		margin: 12px 0px 0px 0px;
		font-size: 2rem;
		color: #172554;
	}

	.empty-state p {
		margin: 12px 0px 0px 0px;
		color: #475569;
	}

	.super-bundle-grid {
		display: grid;
		grid-template-columns: repeat(1, minmax(0, 1fr));
		gap: 26px 22px;
	}

	.super-bundle-card {
		position: relative;
		border: 1px solid rgba(191, 219, 254, 0.85);
		border-radius: 26px;
		background: #ffffff;
		box-shadow: 0px 24px 50px -42px rgba(15, 23, 42, 0.6);
		overflow: visible;
	}

	.card-thumb-link {
		display: block;
		text-decoration: none;
	}

	.card-collage {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 10px;
		padding: 16px;
		background: linear-gradient(180deg, rgba(224, 231, 255, 0.82), rgba(255, 255, 255, 0.95));
		border-top-left-radius: 26px;
		border-top-right-radius: 26px;
	}

	.card-collage-cell {
		aspect-ratio: 1 / 1;
		border-radius: 18px;
		overflow: hidden;
		background: linear-gradient(135deg, rgba(203, 213, 225, 0.55), rgba(241, 245, 249, 0.95));
	}

	.card-collage-cell img,
	.card-collage-fallback {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.card-body {
		padding: 0px 18px 20px 18px;
	}

	.card-heading-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		margin-top: 10px;
	}

	.card-bundle-count {
		margin: 0;
		font-size: 0.95rem;
		font-weight: 700;
		color: #334155;
	}

	.card-title {
		margin: 10px 0px 0px 0px;
		font-size: 1.45rem;
		line-height: 1.2;
	}

	.card-title a {
		color: #172554;
		text-decoration: none;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		overflow: hidden;
	}

	.card-title a:hover {
		color: #1d4ed8;
	}

	.price-row {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: 10px 12px;
		margin-top: 12px;
	}

	.price-current {
		font-size: 1.5rem;
		font-weight: 800;
		color: #0f766e;
	}

	.price-raw-struck {
		font-size: 1.1rem;
		font-weight: 700;
		color: #dc2626;
		text-decoration: line-through;
		text-decoration-thickness: 2px;
		text-decoration-color: rgba(220, 38, 38, 0.9);
	}

	.savings-row {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: 10px 14px;
		margin-top: 10px;
		font-size: 0.95rem;
		font-weight: 700;
		color: #1d4ed8;
	}

	.owned-line {
		margin: 14px 0px 0px 0px;
		padding: 12px 14px;
		border-radius: 18px;
		background: rgba(224, 231, 255, 0.58);
		font-size: 0.95rem;
		font-weight: 600;
		color: #1e3a8a;
	}

	.bundle-preview-list {
		display: grid;
		gap: 8px;
		margin-top: 16px;
	}

	.bundle-preview-item {
		display: flex;
		align-items: center;
		gap: 10px;
		min-width: 0;
	}

	.bundle-preview-item-muted {
		color: #64748b;
	}

	.bundle-preview-dot {
		width: 8px;
		height: 8px;
		border-radius: 999px;
		background: #14b8a6;
		flex: 0 0 auto;
	}

	.bundle-preview-text {
		display: block;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: #334155;
	}

	.card-actions {
		display: flex;
		margin-top: 20px;
	}

	.button-primary {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		border-radius: 999px;
		padding: 12px 18px;
		font-weight: 800;
		text-decoration: none;
		transition: transform 0.18s ease, box-shadow 0.18s ease;
		background: linear-gradient(135deg, #0f766e, #0ea5a4);
		color: #ffffff;
		box-shadow: 0px 16px 28px -22px rgba(15, 118, 110, 0.9);
	}

	.button-primary:hover {
		transform: translateY(-1px);
	}

	.ribbon-wrap {
		position: absolute;
		right: -36px;
		top: 18px;
		width: 130px;
		-ms-transform: rotate(-45deg);
		-webkit-transform: rotate(-45deg);
		transform: rotate(-45deg);
		border-left: 25px solid transparent;
		border-right: 25px solid transparent;
		border-bottom: 25px solid #ff4444;
		box-shadow: 0px 6px 5px -5px rgba(0, 0, 0, 0.5);
		z-index: 10;
	}

	.ribbon-wrap.a-right {
		-ms-transform: rotate(45deg);
		-webkit-transform: rotate(45deg);
		transform: rotate(45deg);
	}

	.ribbon-wrap .ribbon-content {
		position: absolute;
		left: 17px;
		top: 3px;
		font-size: 15px;
		font-weight: bold;
		line-height: 19px;
		color: white;
		width: auto;
		text-align: center;
		word-spacing: -1px;
	}

	@media all and (min-width: 768px) {
		#super-bundles-page {
			padding: 32px 24px 64px 24px;
		}
	}

	@media all and (min-width: 1200px) {
		.super-bundle-grid {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}

	@media all and (min-width: 768px) and (max-width: 1199px) {
		.super-bundle-grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
</style>
